import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { Course } from './types';
import { priceDiffersFromGroup } from './pricing';

const styles = StyleSheet.create({
  card: {
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  leftSection: {
    flex: 1,
    paddingRight: 8,
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    fontSize: 11.5,
    lineHeight: '14.5px',
    marginBottom: 2,
    color: '#CD1D8D',
    fontWeight: 400,
    // flex-basis 0 + grow gives the title a bounded width so a long title wraps
    // instead of overflowing into the price.
    flex: 1,
  },
  priceSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
    // Keep the price on one line; the title shrinks around it.
    flexShrink: 0,
  },
  priceText: {
    fontSize: 10,
    lineHeight: '15px',
    color: '#171923',
    fontWeight: 400,
  },
  description: {
    fontSize: 10,
    lineHeight: '15px',
    marginBottom: 6,
    color: '#171923',
    fontWeight: 400,
  },
  languages: {
    fontSize: 9,
    lineHeight: '13px',
    color: '#4A5568',
    fontWeight: 400,
  },
  languageNote: {
    fontSize: 9,
    lineHeight: '13px',
    marginBottom: 6,
    color: '#4A5568',
    fontWeight: 400,
  },
});

export function CourseCard({
  course,
  groupFullCoursePrice,
}: {
  course: Course;
  groupFullCoursePrice?: number;
}) {
  const isQualification = course.kind === 'QUALIFICATION';
  // Qualifications normally show only the group price header. Show a course's own
  // price inline when it differs from that group price
  const showPrice = !isQualification || priceDiffersFromGroup(course, groupFullCoursePrice);

  const courseCodeIsPresent = (course.code ?? '').trim() !== '';
  const titleText = courseCodeIsPresent ? `${course.code} - ${course.title}` : course.title;

  return (
    <View style={styles.card} wrap={false}>
      <View style={styles.leftSection}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>{titleText}</Text>

          {showPrice ? (
            <View style={styles.priceSection}>
              {isQualification ? (
                <Text style={styles.priceText}>${course.semesterPrice} Single Semester</Text>
              ) : null}
              <Text style={styles.priceText}>${course.fullCoursePrice} Full Course</Text>
              {!isQualification ? (
                <Text style={styles.priceText}>Duration {course.duration}</Text>
              ) : null}
            </View>
          ) : null}
        </View>

        <Text style={styles.description}>{course.description}</Text>

        {course.languages != null && course.languages.length > 0 ? (
          <>
            <Text style={styles.languages}>
              Learning content available in {course.languages.join(', ')}
            </Text>
            <Text style={styles.languageNote}>Assessments must be completed in English</Text>
          </>
        ) : null}
      </View>
    </View>
  );
}
